/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, ReactNode, useCallback, useEffect, useRef, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {cloneDeep, debounce} from 'lodash';
import isEqual from 'lodash.isequal';
import isNil from 'lodash.isnil';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {Crumb} from '@ion/core/src/types/entities';
import {CheckboxState} from '@ion/core/src/types/general';
import {isObjectEmpty} from '@ion/core/src/utils/ObjectUtil';
import {defineBreadcrumbs} from '@ion/dls/src/components/breadcrumbs/BreadcrumbUtils';
import FilterMegaMenu from '@ion/dls/src/components/menus/FilterMegaMenu';
import MobileActionContent from '@ion/dls/src/components/menus/MobileActionContent';
import Hidden from '@ion/dls/src/layout/Hidden';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {resetValue} from '@ion/dls/src/utils/ComponentUtils';
import CheckBox from '../components/checkbox/CheckBox';
import {ClickAwayListener} from '../components/listeners/ClickAwayListener';
import {useLoadFilters} from '../hooks/useLoadFilters';
import MaterialBackIcon from '../icons/MaterialBackIcon';
import {FilterExpandContent} from './FilterExpandContent';
import {
  createFilterHashMap,
  createFilterOrderedMap,
  getFilterContentOptionsViaGroup,
  getFilterContentOptionsViaSearch,
  getFilterSelectedOptionData,
  getFilterSelectedOptions,
  getFilterSelectedOptionsCount,
  updateFilterHashMapChecked,
  updateFilterHashMapCheckedAll
} from './FilterHelpers';
import {FilterMobileExpandContent} from './FilterMobileExpandContent';

export interface FilterProps {
  elementId?: string;
  optionType: string;
  optionData?: any[];
  optionPropId: string;
  optionPropLabel: string;
  optionPropGroupId?: string;
  optionPropGroupLabel?: string;
  optionPropGroupMembers?: string;
  optionPropGroupMember?: string;
  selectedOptionIds?: (number | string)[];
  loading?: boolean;
  showSelectedOptionsCount?: boolean;
  includeGroupsInSelectedOptionsCount?: boolean;
  actionLabel?: string;
  onActionClick?: Function;
  primaryButtonDisabled?: boolean;
  secondaryButtonDisabled?: boolean;
  onSubmit: (val: any) => void;
  onCancel?: () => void;
  onMenuClose?: () => void;
  onOpenClose?: (isOpen: boolean) => void;
  menuLeft?: string;
  menuWidth?: string;
  menuHeight?: string;
  menuPaddingBottom?: string;
  contentGridTemplateColumns?: string;
  contentJustifyContent?: string;
  contentGridColumnGap?: string;
  contentGridRowGap?: string;
  disabled?: boolean;
  loadFilterOptions?: (accountId) => Promise<{ result: any; filterResult: any }>;
  hasErrors?: boolean;
  accountId?: number | number[];
  isMobileView?: boolean;
  selectedAccount?: any;
  refresh?: boolean;
  updateRefresh?: Function;
  children?: any;
  isTree?: boolean;
  onFilterClose?: () => void;
  selectedCount?: number;
  additionalFeatures?: AdditionalFeatures;
}

export interface AdditionalFeatures {
  permission: boolean;
  state: boolean;
  handler: Function;
  label: string;
}

interface StyleProps {
  menuContentPadding?: string;
  useDefaultHeight?: boolean;
};

export const useStyles = makeStyles((theme: DLSTheme) => {
  const {
    dls: {
      motion: {
        element: {
          largeExpansion: {
            close: expansionClose,
            open: expansionOpen
          }
        }
      }
    }
  } = theme;
  return {
    menuContent : {
      display                        : 'flex',
      flexDirection                  : 'column',
      paddingTop                     : '0px',
      [theme.breakpoints.down('sm')] : {
        padding : '0px 0px 4px 0px'
      }
    },
    fullViewContent : {
      height                       : 'inherit',
      overflow                     : 'auto',
      flexDirection                : 'row',
      maxHeight                    : 400,
      [theme.breakpoints.up('sm')] : {
        overflowY : 'auto',
        padding   : ({menuContentPadding}: StyleProps) => menuContentPadding ?? 'auto'
      },
      [theme.breakpoints.down('sm')] : {
        height        : ({useDefaultHeight}: StyleProps) => useDefaultHeight ? 400 : 450,
        flexDirection : 'column',
        alignContent  : 'baseline'
      },
      gap : 10
    },
    menuContentDetails : {
      width                          : '100%',
      [theme.breakpoints.down('sm')] : {
        maxHeight : 300,
        order     : 2,
        height    : 'inherit',
        overflowY : 'auto'
      }
    },
    menuContentCtas : {
      width          : '40%',
      display        : 'flex',
      flexDirection  : 'row',
      flexWrap       : 'wrap',
      justifyContent : 'flex-end',
      alignContent   : 'baseline',
      '& div'        : {
        marginRight : 20
      },
      [theme.breakpoints.down('sm')] : {
        width : '100%',
        order : 1
      }
    },
    filterHeaderBar : {
      display                        : 'flex',
      justifyContent                 : 'space-between',
      alignItems                     : 'center',
      marginBottom                   : '17px',
      minHeight                      : '58px',
      [theme.breakpoints.down('sm')] : {
        display  : 'block',
        position : 'relative',
        '& span' : {
          top   : '20px !important',
          right : '46px !important'
        }
      }
    },
    groupAssetsFilterHeaderBar : {
      display                        : 'flex',
      justifyContent                 : 'space-between',
      alignItems                     : 'center',
      minHeight                      : '48px',
      [theme.breakpoints.down('sm')] : {
        display       : 'flex',
        flexDirection : 'column-reverse',
        alignItems    : 'flex-end',
        '& span'      : {
          top   : '20px !important',
          right : '46px !important'
        }
      }
    },
    chevron : {
      fill       : dlsColor('grey3'),
      padding    : '8px 0 8px 8px',
      transition : 'transform 300ms ease-in-out',
      '&:hover'  : {
        transform  : 'translateX(-3px)',
        transition : 'transform 300ms ease-in-out'
      }
    },
    activeCrumb : {
      color : dlsColor('grey1')
    },
    inactiveCrumb : {
      color     : dlsColor('grey3'),
      '&:hover' : {
        cursor : 'pointer'
      }
    },
    filterContainerHeader : {
      ...dlsAlt('typography', 'label1'),
      marginTop    : 16,
      marginBottom : 20,
      color        : dlsColor('grey1')
    },
    groupAssetsfilterContainerHeader : {
      ...dlsAlt('typography', 'label1'),
      width        : '100%',
      marginTop    : 16,
      marginBottom : 20,
      color        : dlsColor('grey1')
    },
    contentContainer : {
      width : '98.6%'
    },
    chipsContainer : {
      marginBottom                   : '30px',
      [theme.breakpoints.down('sm')] : {
        marginBottom : '10px'
      }
    },
    crumbsContainer : {
      marginBottom : '0px'
    },
    label : {
      ...dlsAlt('typography', 'label1'),
      color : dlsColor('grey3')
    },
    mobileHeader : {
      display    : 'flex',
      alignItems : 'center'
    },
    backButton : {
      marginRight : '8px'
    },
    crumbList : {
      fontSize   : '14px',
      textShadow : `0 0 ${dlsColor('grey4')}`
    },
    autoCoompleteRoot : {
      minWidth : 250,
      display  : 'flex'
    },
    popper : {
      zIndex : 6000
    },
    filterSelectWrapperHidden : {
      transition : `display 500ms ease-out, opacity ${expansionClose}`,
      display    : 'none'
    },
    filterSelectWrapper : {
      maxWidth   : '300px',
      transition : `max-width ${expansionOpen}, opacity ${expansionClose}`,
      '& span'   : {
        ...dlsAlt('typography', 'cta2'),
        cursor : 'pointer'
      },
      '& .selectAll' : {
        color   : dlsColor('aaBlue'),
        margin  : '0 25px',
        opacity : '1'
      },
      '& .clearAll' : {
        color   : dlsColor('grey2'),
        opacity : '1'
      }
    },
    hide : {
      display : 'none'
    }
  };
});

export const treeCheckBox = ({label, state, handler}) => (
  <CheckBox label={label} checked={state} onChange={handler}/>
);

/**
 * The Filter component is the generalized base component for managing the
 * filtering of different iOn entities, and can be custom-styled with props.
 *
 * ```typescript
 *
 * import Filter from '@ion/dls/src/filters';
 *
 * <Filter elementId={elementId}
 *   optionType={optionType}
 *   optionData={optionData}
 *   optionPropId={optionPropId}
 *   optionPropLabel={optionPropLabel}
 *   optionPropGroupId={optionPropGroupId}
 *   optionPropGroupLabel={optionPropGroupLabel}
 *   optionPropGroupMembers={optionPropGroupMembers}
 *   optionPropGroupMember={optionPropGroupMember}
 *   loading={loading}
 *   selectedOptionIds={selectedOptionIds}
 *   showSelectedOptionsCount={showSelectedOptionsCount}
 *   includeGroupsInSelectedOptionsCount={includeGroupsInSelectedOptionsCount}
 *   actionLabel={actionLabel}
 *   onActionClick={onActionClick}
 *   primaryButtonDisabled={primaryButtonDisabled}
 *   secondaryButtonDisabled={secondaryButtonDisabled}
 *   onSubmit={handleSubmit}
 *   onCancel={handleSubmit}
 *   onMenuClose={handleMenuClose}
 *   menuLeft='8.4%'
 *   menuWidth='83.5%'
 *   menuHeight='560px'
 *   menuPaddingBottom='0px'
 *   contentGridTemplateColumns='repeat(5, 1fr)'
 *   contentJustifyContent='center'
 *   contentGridColumnGap='10px'
 *   contentGridRowGap='14px' />
 *
 * ```
 */
export const Filter = ({
  elementId,
  optionType,
  optionPropId,
  optionPropLabel,
  menuLeft,
  menuWidth,
  menuPaddingBottom,
  contentGridTemplateColumns,
  contentJustifyContent,
  contentGridColumnGap,
  contentGridRowGap,
  actionLabel,
  onActionClick,
  accountId,
  selectedAccount,
  refresh,
  updateRefresh,
  onSubmit,
  onCancel,
  onMenuClose,
  onOpenClose,
  loadFilterOptions,
  children,
  additionalFeatures,
  selectedCount = null,
  menuHeight = 'auto',
  optionPropGroupId = 'groupId',
  optionPropGroupLabel = 'groupLabel',
  optionPropGroupMembers = 'groupMembers',
  optionPropGroupMember = 'groupMember',
  selectedOptionIds = [],
  loading = false,
  showSelectedOptionsCount = false,
  includeGroupsInSelectedOptionsCount = false,
  primaryButtonDisabled = false,
  secondaryButtonDisabled = false,
  hasErrors = false,
  isMobileView = false,
  isTree = false,
  optionData = null,
  disabled = null,
  onFilterClose = null
}: FilterProps) => {
  const classes      = useStyles();
  const crumbClasses = {
    activeCrumb   : classes.activeCrumb,
    inactiveCrumb : classes.inactiveCrumb
  };

  const rawCrumbs                                                   = useRef([]);
  const [isOpen, setIsOpen]                                         = useState(isMobileView);
  const [crumbData, setCrumbData]                                   = useState<Crumb[]>([]);
  const [filterHashMap, setFilterHashMap]                           = useState({});
  const [filterOrderedMap, setFilterOrderedMap]                     = useState([]);
  const filterContentGroupId                                        = useRef(null);
  const [filterSelectedOptions, setFilterSelectedOptions]           = useState([]);
  const filterSelectedOptionsCount                                  = useRef(0);
  const [filterContentOptions, setFilterContentOptions]             = useState([]);
  const [filterSavedHashMap, setFilterSavedHashMap]                 = useState({});
  const [filterSavedOrderedMap, setFilterSavedOrderedMap]           = useState([]);
  const [filterSavedSelectedOptions, setFilterSavedSelectedOptions] = useState([]);
  const filterSavedSelectedOptionsCount                             = useRef(0);
  const [filterClearSearch, setFilterClearSearch]                   = useState(false);
  const [filterSearchValue, setFilterSearchValue]                   = useState('');
  const isMounted                                                   = useHasMounted();
  const accountIdRef                                                = useRef(null);
  const hasSelection                                                = !!filterSelectedOptions?.length;
  const {
    fetchData,
    options,
    filterOptions,
    errors: hasError,
    loading: loadingOnFetch
  } = useLoadFilters({
    hasErrors,
    accountId        : accountIdRef.current,
    isLoading        : loading,
    getFilterOptions : loadFilterOptions,
    type             : optionType
  });

  useEffect(() => { onOpenClose?.(isOpen); }, [isOpen]);

  useEffect(function() {
    if (
      isObjectEmpty(options) &&
      isObjectEmpty(filterOptions) &&
      isOpen) {
      fetchData();
    }
  }, [isOpen]);

  useEffect(function() {
    if (!isEqual(accountId, accountIdRef.current)) {
      accountIdRef.current = accountId;
      fetchData();
    }
  }, [accountId, selectedAccount]);

  useEffect(function() {
    if (refresh) {
      fetchData();
      updateRefresh?.();
    }
  }, [refresh]);

  useEffect(() => {
    const hashMap = createFilterHashMap({
      optionData : isNil(optionData) ? filterOptions as any[]: optionData,
      optionPropId,
      optionPropLabel,
      optionPropGroupId,
      optionPropGroupLabel,
      optionPropGroupMembers,
      optionPropGroupMember,
      selectedOptionIds
    });
    setFilterHashMap(hashMap);
    setFilterSavedHashMap(cloneDeep(hashMap));
    if (!rawCrumbs.current?.[0]?.label) {
      rawCrumbs.current = [{
        label : optionType,
        click : false
      }];
      setCrumbData(defineBreadcrumbs({
        crumbs  : rawCrumbs.current,
        classes : crumbClasses
      }));
    }
  }, [optionData, selectedOptionIds, options, filterOptions]);

  useEffect(() => {
    if (isMounted) {
      updateOrderedMap();
    }
  }, [filterHashMap]);

  useEffect(() => {
    if (isMounted) {
      updateSelectedOptions();
      updateContentOptions();
    }
  }, [filterOrderedMap]);

  useEffect(() => {
    if (isMounted) {
      updateContentOptions();
    }
  }, []);

  useEffect(() => {
    if (isMounted) {
      updateSavedOrderedMap();
    }
  }, [filterSavedHashMap]);

  useEffect(() => {
    if (isMounted) {
      updateSavedSelectedOptions();
    }
  }, [filterSavedOrderedMap]);

  const updateOrderedMap = () => {
    const updated = createFilterOrderedMap(filterHashMap);
    setFilterOrderedMap(updated);
  };

  const updateSelectedOptions = () => {
    const updated = getFilterSelectedOptions(filterOrderedMap);
    setFilterSelectedOptions(updated);
    filterSelectedOptionsCount.current = getFilterSelectedOptionsCount(
      filterOrderedMap,
      includeGroupsInSelectedOptionsCount);
  };

  const updateContentOptions = () => {
    const updated = (filterSearchValue.length >= 3)
      ? getFilterContentOptionsViaSearch(filterOrderedMap, filterSearchValue)
      : getFilterContentOptionsViaGroup(filterOrderedMap,
        entityId(filterContentGroupId.current) ?? filterContentGroupId.current);
    setFilterContentOptions(updated);
  };

  const updateSavedOrderedMap = () => {
    const updated = createFilterOrderedMap(filterSavedHashMap);
    setFilterSavedOrderedMap(updated);
  };

  const updateSavedSelectedOptions = () => {
    if (isOpen) {
      const updated = getFilterSelectedOptions(filterSavedOrderedMap);
      setFilterSavedSelectedOptions(updated);
      const count = getFilterSelectedOptionsCount(
        filterSavedOrderedMap,
        includeGroupsInSelectedOptionsCount);
      filterSavedSelectedOptionsCount.current = count;
    } else {
      filterSavedSelectedOptionsCount.current = (new Set(selectedOptionIds)).size;
    }
  };

  function clearSearchValue() {
    resetValue({
      fnCall       : setFilterClearSearch,
      initialValue : true,
      resetValue   : false
    });
    handleSearchValueChange('');
  };

  function handleSearchValueChange(value: string) {
    let updated;
    if (value.length > 0) {
      updated = getFilterContentOptionsViaSearch(filterOrderedMap, value);
    } else {
      updated = getFilterContentOptionsViaGroup(
        filterOrderedMap,
        entityId(filterContentGroupId.current) ?? filterContentGroupId.current
      );
    };
    setFilterSearchValue(value);
    setFilterContentOptions(updated);
  };

  function handleChipDelete(itemId: string) {
    handleContentOptionSelected(itemId, false);
  }

  function handleToggleSelectAll(checked: boolean) {
    const checkedState = checked ? CheckboxState.Checked : CheckboxState.Unchecked;
    const updatedHashMap = updateFilterHashMapCheckedAll(
      checkedState,
      filterHashMap
    );
    setFilterHashMap(updatedHashMap);
  }

  const handleContentOptionSelected = useCallback(function(itemId: string, checked: boolean) {
    const checkedState = checked ? CheckboxState.Checked : CheckboxState.Unchecked;
    const updatedHashMap = updateFilterHashMapChecked(
      itemId,
      checkedState,
      filterHashMap
    );
    setFilterHashMap(updatedHashMap);
  }, [filterHashMap]);

  function handleContentGroupSelected(itemId: string) {
    const groupIdChain = [];
    if (itemId) {
      groupIdChain.unshift(itemId);
      let groupItem = filterHashMap[itemId];
      while (groupItem.parentId) {
        groupIdChain.unshift(groupItem.parentId);
        groupItem = filterHashMap[groupItem.parentId];
      }
    }
    rawCrumbs.current = [{
      label   : optionType,
      click   : true,
      onClick : function() {
        handleCrumbClick(0);
      }
    }].concat(groupIdChain.map((groupId, index) => {
      return {
        label   : filterHashMap[groupId].title,
        click   : true,
        onClick : function() {
          handleCrumbClick(index + 1);
        }
      };
    }));
    setCrumbData(defineBreadcrumbs({
      crumbs  : rawCrumbs.current,
      classes : crumbClasses
    }));
    filterContentGroupId.current = itemId;
    const updatedContentOptions = getFilterContentOptionsViaGroup(
      filterOrderedMap,
      entityId(filterContentGroupId.current) ?? filterContentGroupId.current
    );
    setFilterContentOptions(updatedContentOptions);
  }

  function handleCrumbClick(level: number) {
    const diff = (rawCrumbs.current.length - 1) - level;
    rawCrumbs.current.splice(rawCrumbs.current.length - diff, diff);
    setCrumbData(defineBreadcrumbs({
      crumbs  : rawCrumbs.current,
      classes : crumbClasses
    }));
    for (let i = 0; i < diff; i++) {
      const item = filterHashMap[filterContentGroupId.current];
      filterContentGroupId.current = item.parentId;
    }
    const updatedContentOptions = getFilterContentOptionsViaGroup(
      filterOrderedMap,
      filterContentGroupId.current
    );
    setFilterContentOptions(updatedContentOptions);
  }

  function handleCrumbBack() {
    rawCrumbs.current.pop();
    setCrumbData(defineBreadcrumbs({
      crumbs  : rawCrumbs.current,
      classes : crumbClasses
    }));
    const item = filterHashMap[filterContentGroupId.current];
    filterContentGroupId.current = item.parentId;
    const updatedContentOptions = getFilterContentOptionsViaGroup(
      filterOrderedMap,
      filterContentGroupId.current
    );
    setFilterContentOptions(updatedContentOptions);
  }

  const handleSubmit = useCallback(function() {
    const clonedMap = cloneDeep(filterHashMap);
    setFilterSavedHashMap(clonedMap);
    clearSearchValue();
    onMenuClose?.();
    onSubmit({
      selections : getFilterSelectedOptionData(filterOrderedMap),
      options,
      loading    : loadingOnFetch,
      hasError   : hasError
    });
  }, [filterHashMap, filterOrderedMap, hasError, loadingOnFetch, options]);

  const handleCancel = useCallback(function() {
    setFilterHashMap(cloneDeep(filterSavedHashMap));
    clearSearchValue();
    onMenuClose?.();
    onCancel?.();
  }, [filterSavedHashMap]);

  const setMenuCurrentState = useCallback(function(currentState) {
    if (isOpen && !currentState) {
      onFilterClose?.();
    }
    setIsOpen(currentState);
  }, [onFilterClose, isOpen]);

  const onClickAway = useCallback(function() {
    setIsOpen(false);
    onFilterClose?.();
  }, [isOpen, onFilterClose]);

  const treeProps = {
    isFilterOpen : isOpen
  };

  return (
    <>
      <Hidden show='desktop'>
        <FilterMegaMenu elementId={elementId}
          selectFieldValue={optionType}
          onMenuClose={handleCancel}
          disabled={isNil(disabled) ? Boolean(hasError) : disabled}
          numberOptionsSelected={selectedCount || ((filterSavedSelectedOptionsCount.current > 0)
            ? filterSavedSelectedOptionsCount.current
            : null)
          }
          selectFieldMinWidth='0px'
          minMenuWidth='0px'
          menuWidth={menuWidth}
          menuLeft={menuLeft}
          additionalFeatures={additionalFeatures}
          menuHeight={menuHeight}
          menuPaddingBottom={menuPaddingBottom}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          actionLabel={actionLabel}
          onActionClick={onActionClick}
          primaryButtonDisabled={primaryButtonDisabled}
          secondaryButtonDisabled={secondaryButtonDisabled}
          loading={loading}
          setMenuCurrentState={setMenuCurrentState}>
          {!children && isOpen && (<FilterExpandContent classes={classes}
            children={children}
            treeProps={treeProps}
            rawCrumbs={rawCrumbs.current}
            optionType={optionType}
            hasSelection={hasSelection}
            filterHashMap={filterHashMap}
            contentGridRowGap={contentGridRowGap}
            filterClearSearch={filterClearSearch}
            contentGridColumnGap={contentGridColumnGap}
            filterSelectedOptions={filterSelectedOptions}
            contentJustifyContent={contentJustifyContent}
            showSelectedOptionsCount={showSelectedOptionsCount}
            filterSelectedOptionsCount={filterSelectedOptionsCount.current}
            contentGridTemplateColumns={contentGridTemplateColumns}
            crumbData={crumbData as any}
            filterSearchValue={filterSearchValue}
            filterContentOptions={filterContentOptions}
            handleCrumbBack={handleCrumbBack}
            handleChipDelete={handleChipDelete}
            clearSearchValue={clearSearchValue}
            setFilterClearSearch={setFilterClearSearch}
            handleToggleSelectAll={handleToggleSelectAll}
            handleSearchValueChange={handleSearchValueChange}
            handleContentGroupSelected={handleContentGroupSelected}
            handleContentOptionSelected={handleContentOptionSelected} />)}
          {!!children && children}
        </FilterMegaMenu>
      </Hidden>
      <Hidden show='mobile'>
        <ClickAwayListener onClickAway={onClickAway}
          mountListener>
          <MobileActionContent onSubmit={handleSubmit}
            primaryButtonDisabled={primaryButtonDisabled}>
            <div className={classes.menuContent}>
              <div className={classes.mobileHeader}>
                <span className={classes.backButton}
                  onClick={handleCancel}>
                  <MaterialBackIcon />
                </span>
                <span className={classes.label}>{optionType}</span>
              </div>
              {!children && (<FilterMobileExpandContent isTree={isTree}
                classes={classes}
                children={children}
                treeProps={treeProps}
                rawCrumbs={rawCrumbs.current}
                optionType={optionType}
                hasSelection={hasSelection}
                filterHashMap={filterHashMap}
                contentGridRowGap={contentGridRowGap}
                filterClearSearch={filterClearSearch}
                contentGridColumnGap={contentGridColumnGap}
                filterSelectedOptions={filterSelectedOptions}
                contentJustifyContent={contentJustifyContent}
                showSelectedOptionsCount={showSelectedOptionsCount}
                filterSelectedOptionsCount={filterSelectedOptionsCount.current}
                contentGridTemplateColumns={contentGridTemplateColumns}
                crumbData={crumbData as any}
                filterSearchValue={filterSearchValue}
                filterContentOptions={filterContentOptions}
                handleCrumbBack={handleCrumbBack}
                handleChipDelete={handleChipDelete}
                clearSearchValue={clearSearchValue}
                setFilterClearSearch={setFilterClearSearch}
                handleToggleSelectAll={handleToggleSelectAll}
                handleSearchValueChange={handleSearchValueChange}
                handleContentGroupSelected={handleContentGroupSelected}
                handleContentOptionSelected={handleContentOptionSelected} />)}
              {!!children && children}
              {additionalFeatures?.permission && treeCheckBox(additionalFeatures)}
            </div>
          </MobileActionContent>
        </ClickAwayListener>
      </Hidden>
    </>
  );
};

export default Filter;
